/* eslint-disable no-console */
import type { Plugin, Context } from '@nuxt/types';
import type { Location } from 'vue-router';

export enum Features {
  b2c_onboarding_signup_users,
  b2c_onboarding_redirect_members_to_onboarding,
}
export type FeatureFlag = keyof typeof Features;

export const getUtils = (ctx: Context) => {
  function keepQuery(location: Location, extra: Location = {}) {
    const query = {
      ...(ctx.route.query || {}),
      ...(location.query || {}),
      ...(extra.query || {}),
    };

    const params = {
      ...(ctx.route.params || {}),
      ...(location.params || {}),
      ...(extra.params || {}),
    };

    return { ...location, query, params };
  }

  function nuxtErrorPage(error: unknown) {
    const sentryError = ctx.$sentryUtils.error(error, { place: 'nuxtErrorPage' });
    if (!sentryError) return;
    ctx.error(sentryError);
  }

  const routeReplaceWithQuery = async (location: Location, extra?: Location) =>
    await ctx.app.router?.replace(keepQuery(location, extra));

  const routePushWithQuery = async (location: Location, extra?: Location) =>
    await ctx.app.router?.push(keepQuery(location, extra));

  const redirectWithQuery = (location: Location, extra?: Location, statusCode = 302) =>
    ctx.redirect(statusCode, keepQuery(location, extra));

  /**
   * Return raw data instead of Observer
   * TODO: move to development utils
   */
  const toRaw = (data: unknown) => JSON.parse(JSON.stringify(data));

  /**
   * Is value null or underfined
   * Helper for templates, cuz we don't have nullish coalescing operator in vue templates.
   * FIXME: we could move such functions to $template or smth like that
   */
  const isNullish = (value: unknown) => value === null || typeof value === 'undefined';

  const isFeatureEnabled = (feature: FeatureFlag): boolean =>
    ctx.$auth.user?.enabledFeatures?.some(f => f.key === feature) || false;

  return {
    nuxtErrorPage,
    routeReplaceWithQuery,
    routePushWithQuery,
    redirectWithQuery,
    toRaw,
    isNullish,
    isFeatureEnabled,
  };
};

const utilsPlugin: Plugin = (ctx, inject) => {
  const utils = getUtils(ctx);
  inject('utils', utils);
};

export default utilsPlugin;
